import { Description, Field, Listbox, ListboxButton, ListboxOption, ListboxOptions, Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { IconCalendar, IconCheck, IconChevronDown, IconChevronLeft, IconChevronRight, IconClock } from '@tabler/icons-react';
import { useId, useLayoutEffect, useRef, useState } from 'react';
import type { RespondentField } from '../../lib/multimodal-contract';
import { isDateQuestionDayAllowed, resolveDateRuleBounds, type DateRuleBounds, isDateTimeQuestionValueInRange, normalizeDateTimeQuestionConfig, validateDateTimeQuestionAnswer, type DateTimeAnswerValue } from '../../lib/question-blocks';
import { ConversationTheme, useConversationPalette } from './ConversationTheme';
import './DateTimeQuestionControl.css';

type DateConfig = NonNullable<RespondentField['dateTime']>;

/** Calendar and time choices share the same draft and submission boundary. */
interface DateTimeQuestionControlProps {
  config: DateConfig;
  bounds?: DateRuleBounds;
  value?: DateTimeAnswerValue;
  onChange: (value: DateTimeAnswerValue) => void;
  onSubmit: (value: DateTimeAnswerValue) => void;
  disabled: boolean;
  labelledBy: string;
  submitLabel: string;
}

const focus = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-volt';
const nativeTheme = '[color-scheme:light] dark:[color-scheme:dark]';
const button = `${focus} cursor-pointer rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-40 aria-disabled:cursor-not-allowed aria-disabled:opacity-40`;
const dateFormat = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'long', year: 'numeric' });
const fullDateFormat = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const months = Array.from({ length: 12 }, (_, index) => ({
  value: String(index + 1).padStart(2, '0'),
  label: new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', month: 'long' }).format(new Date(`2020-${String(index + 1).padStart(2, '0')}-01T12:00:00Z`)),
}));
const hours = Array.from({ length: 24 }, (_, index) => String(index).padStart(2, '0'));
const minutes = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, '0'));

/** A date/time menu uses page-rendered options instead of an OS-owned popup. */
interface DateTimeSelectProps {
  label: string;
  value: string;
  options: { value: string; label: string; disabled?: boolean }[];
  onChange: (value: string) => void;
  disabled: boolean;
  invalid?: boolean;
  error?: string;
}

/** Keep bounded menus themed, keyboard accessible, and safe inside the calendar. */
function DateTimeSelect({ label, value, options, onChange, disabled, invalid = false, error }: DateTimeSelectProps) {
  const selected = options.find(option => option.value === value);
  const description = [selected?.label, error].filter(Boolean).join(' ');
  return <Field className="contents">
    <Listbox value={value} onChange={onChange} disabled={disabled}>
      <ListboxButton aria-label={label} aria-invalid={invalid}
        className={`${focus} date-time-select`}>
        <span className="min-w-0 flex-1 truncate text-left">{selected?.label ?? label}</span>
        <IconChevronDown aria-hidden="true" size={16} className="shrink-0 text-muted-foreground" />
      </ListboxButton>
      <ListboxOptions anchor={{ to: 'bottom start', gap: 4, padding: 16 }} modal={false} transition
        aria-label={label} className="date-time-menu scrollbar-dark">
        {options.map(option => <ListboxOption key={option.value} value={option.value} disabled={option.disabled}
          className="date-time-option">
          {({ selected: isSelected }) => <>
            <span className="min-w-0 flex-1">{option.label}</span>
            {isSelected ? <IconCheck aria-hidden="true" size={16} className="shrink-0" /> : null}
          </>}
        </ListboxOption>)}
      </ListboxOptions>
    </Listbox>
    {description ? <Description className="sr-only">{description}</Description> : null}
  </Field>;
}

/** Use noon UTC for calendar arithmetic, never a timezone-shifted local midnight. */
function calendarDate(day: string): Date { return new Date(`${day}T12:00:00Z`); }

/** Keep the initial calendar near the person's local day. */
function today(): string {
  const date = new Date();
  return `${String(date.getFullYear()).padStart(4, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** Calendar day arithmetic also supports leap days and years below 100. */
function shiftDay(day: string, offset: number): string {
  const date = calendarDate(day);
  date.setUTCDate(date.getUTCDate() + offset);
  if (date.getUTCFullYear() < 1) return '0001-01-01';
  if (date.getUTCFullYear() > 9999) return '9999-12-31';
  return date.toISOString().slice(0, 10);
}

/** Preserve a valid saved zone; an explicit edit repairs a missing or invalid zone. */
function answerTimezone(saved?: string): string {
  if (saved) {
    try { new Intl.DateTimeFormat('en-GB', { timeZone: saved }); return saved; }
    catch { /* Invalid saved metadata is corrected only when the person edits. */ }
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

/** Shift from the first day so shorter months cannot overflow. */
function shiftMonth(month: string, offset: number): string {
  const date = calendarDate(`${month}-01`);
  date.setUTCMonth(date.getUTCMonth() + offset);
  return date.toISOString().slice(0, 7);
}

/** A bounded calendar with one tab stop and direct month/year navigation. */
function DateCalendar({ selected, min, max, open, disabled, onSelect, isAllowed }: {
  selected: string; min?: string; max?: string; open: boolean; disabled: boolean; onSelect: (day: string) => void; isAllowed: (day: string) => boolean;
}) {
  const clamp = (day: string): string => day < (min ?? '0001-01-01') ? min ?? '0001-01-01'
    : day > (max ?? '9999-12-31') ? max ?? '9999-12-31' : day;
  const initial = clamp(selected || today());
  const [month, setMonth] = useState(initial.slice(0, 7));
  const [yearDraft, setYearDraft] = useState(initial.slice(0, 4));
  const [focusedDay, setFocusedDay] = useState(initial);
  const dayButtons = useRef(new Map<string, HTMLButtonElement>());
  const shouldFocusDay = useRef(true);
  useLayoutEffect(() => { if (open && shouldFocusDay.current) dayButtons.current.get(focusedDay)?.focus(); }, [focusedDay, open]);
  const first = calendarDate(`${month}-01`);
  const offset = (first.getUTCDay() + 6) % 7;
  const last = calendarDate(`${month}-01`);
  last.setUTCMonth(last.getUTCMonth() + 1);
  last.setUTCDate(0);
  const count = last.getUTCDate();
  const previousDisabled = disabled || month <= (min?.slice(0, 7) ?? '0001-01');
  const nextDisabled = disabled || month >= (max?.slice(0, 7) ?? '9999-12');

  /** Browse without committing a date or disturbing the parked time. */
  const browse = (nextMonth: string, focusDay = false): void => {
    const next = clamp(`${nextMonth}-01`);
    shouldFocusDay.current = focusDay;
    setMonth(next.slice(0, 7)); setYearDraft(next.slice(0, 4)); setFocusedDay(next);
  };
  /** An incomplete year stays editable until blur or Enter. */
  const commitYear = (): void => {
    const year = Number(yearDraft);
    if (/^\d{1,4}$/.test(yearDraft) && year >= 1 && year <= 9999) browse(`${String(year).padStart(4, '0')}-${month.slice(5)}`);
    else setYearDraft(month.slice(0, 4));
  };
  /** Move focus without selecting; Enter and Space retain native button behavior. */
  const navigateDay = (event: React.KeyboardEvent<HTMLButtonElement>, day: string): void => {
    let next: string;
    if (event.key === 'ArrowRight') next = shiftDay(day, 1);
    else if (event.key === 'ArrowLeft') next = shiftDay(day, -1);
    else if (event.key === 'ArrowDown') next = shiftDay(day, 7);
    else if (event.key === 'ArrowUp') next = shiftDay(day, -7);
    else if (event.key === 'Home') next = shiftDay(day, -((calendarDate(day).getUTCDay() + 6) % 7));
    else if (event.key === 'End') next = shiftDay(day, 6 - ((calendarDate(day).getUTCDay() + 6) % 7));
    else if (event.key === 'PageUp' || event.key === 'PageDown') {
      event.preventDefault();
      if (event.key === 'PageUp' ? !previousDisabled : !nextDisabled) browse(shiftMonth(month, event.key === 'PageUp' ? -1 : 1), true);
      return;
    } else return;
    event.preventDefault();
    next = clamp(next);
    shouldFocusDay.current = true;
    setFocusedDay(next); setMonth(next.slice(0, 7)); setYearDraft(next.slice(0, 4));
  };

  return <div className="date-calendar">
    <div className="date-calendar-header">
      <button type="button" aria-label="Previous month" disabled={previousDisabled} className={`${button} date-calendar-arrow hover:bg-surface-hover`}
        onClick={() => browse(shiftMonth(month, -1))}><IconChevronLeft aria-hidden="true" size={18} /></button>
      <DateTimeSelect label="Month" value={month.slice(5)} disabled={disabled}
        onChange={next => browse(`${month.slice(0, 4)}-${next}`)} options={months.map(item => {
          const candidate = `${month.slice(0, 4)}-${item.value}`;
          return { ...item, disabled: candidate < (min?.slice(0, 7) ?? '0001-01') || candidate > (max?.slice(0, 7) ?? '9999-12') };
        })} />
      <input aria-label="Year" type="number" inputMode="numeric" min={Number(min?.slice(0, 4) ?? 1)} max={Number(max?.slice(0, 4) ?? 9999)} disabled={disabled} value={yearDraft}
        className={`${focus} ${nativeTheme} min-w-0 rounded-lg bg-transparent px-1 py-2 text-sm tabular-nums`}
        onChange={event => {
          const next = event.target.value;
          setYearDraft(next);
          if (/^\d{4}$/.test(next) && Number(next) >= 1) browse(`${next}-${month.slice(5)}`);
        }} onBlur={commitYear}
        onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); commitYear(); } }} />
      <button type="button" aria-label="Next month" disabled={nextDisabled} className={`${button} date-calendar-arrow hover:bg-surface-hover`}
        onClick={() => browse(shiftMonth(month, 1))}><IconChevronRight aria-hidden="true" size={18} /></button>
    </div>
    <div aria-hidden="true" className="date-calendar-weekdays">
      {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((label, index) => <span key={index}>{label}</span>)}
    </div>
    <div key={month} role="group" aria-label="Calendar dates" className="date-calendar-days">
      {Array.from({ length: 42 }, (_, index) => {
        const number = index - offset + 1;
        if (number < 1 || number > count) return <span key={index} aria-hidden="true" />;
        const day = `${month}-${String(number).padStart(2, '0')}`;
        const unavailable = disabled || !isAllowed(day);
        return <button key={day} ref={element => { if (element) dayButtons.current.set(day, element); else dayButtons.current.delete(day); }}
          type="button" aria-label={fullDateFormat.format(calendarDate(day))} aria-pressed={day === selected} disabled={disabled || day < (min ?? '0001-01-01') || day > (max ?? '9999-12-31')} aria-disabled={unavailable}
          tabIndex={day === focusedDay ? 0 : -1} className={`${button} date-calendar-day ${day === selected ? 'choice-control' : 'hover:bg-surface-hover'}`}
          onKeyDown={event => navigateDay(event, day)} onClick={() => { if (!unavailable) onSelect(day); }}>{number}</button>;
      })}
    </div>
  </div>;
}

/** Pick dates from a calendar and times from bounded, minute-precise choices. */
export function DateTimeQuestionControl({ config: rawConfig, bounds, value, onChange, onSubmit, disabled, labelledBy, submitLabel }: DateTimeQuestionControlProps) {
  const config = normalizeDateTimeQuestionConfig(rawConfig);
  const palette = useConversationPalette();
  const errorId = useId();
  if (!config) return <p role="alert" className="text-sm text-danger">Date control unavailable</p>;
  const resolved = resolveDateRuleBounds(config, { bounds });
  const context = { bounds: resolved };
  const mode = config.mode;
  const rawValue = value?.value ?? '';
  const candidateDay = mode === 'time' ? '' : rawValue.split('T')[0];
  const day = isDateTimeQuestionValueInRange({ mode: 'date' }, candidateDay) ? candidateDay : '';
  const time = mode === 'date' ? '' : mode === 'time' ? rawValue : rawValue.split('T')[1] ?? '';
  const [hour = '', minute = '', second] = time.split(':');
  const minDay = mode === 'time' ? undefined : [config.min?.slice(0, 10), resolved.min].filter((day): day is string => Boolean(day)).sort().at(-1);
  const maxDay = mode === 'time' ? undefined : [config.max?.slice(0, 10), resolved.max].filter((day): day is string => Boolean(day)).sort().at(0);
  const inRange = isDateTimeQuestionValueInRange(config, rawValue, context);
  const valid = validateDateTimeQuestionAnswer(config, value, context).valid;
  const complete = mode === 'date' ? Boolean(day) : mode === 'time' ? Boolean(hour && minute) : Boolean(day && hour && minute);
  const error = complete && !valid ? inRange ? 'Choose the date and time again.'
    : `Choose a ${mode === 'time' ? 'time' : mode === 'date' ? 'date' : 'date and time'} within the available range.` : '';

  /** Partial choices stay in the local draft; only complete in-range values can submit. */
  const update = (nextDay: string, nextHour = hour, nextMinute = minute, nextSecond = second): void => {
    const nextTime = nextHour || nextMinute ? `${nextHour}:${nextMinute}${nextSecond === undefined ? '' : `:${nextSecond}`}` : '';
    onChange({ value: mode === 'date' ? nextDay : mode === 'time' ? nextTime : `${nextDay}T${nextTime}`,
      ...(mode === 'date_time' ? { timezone: answerTimezone(value?.timezone) } : {}) });
  };
  /** Disable an hour only when none of its minutes fit on the selected day. */
  const allowedTime = (candidateHour: string, candidateMinute?: string, candidateSecond = second): boolean => {
    if (mode === 'date_time' && !day) return true;
    const prefix = mode === 'date_time' ? `${day}T` : '';
    const suffix = candidateSecond === undefined ? '' : `:${candidateSecond}`;
    return candidateMinute === undefined
      ? minutes.some(item => isDateTimeQuestionValueInRange(config, `${prefix}${candidateHour}:${item}${suffix}`, context))
      : isDateTimeQuestionValueInRange(config, `${prefix}${candidateHour}:${candidateMinute}${suffix}`, context);
  };

  return <div className="date-time-control space-y-4" role="group" aria-labelledby={labelledBy}>
    <div className="date-time-fields" data-combined={mode === 'date_time'}>
      {mode !== 'time' ? <Popover className="min-w-0">
        {({ close, open }) => <>
          <PopoverButton disabled={disabled} aria-label={`Choose date${day ? `, ${dateFormat.format(calendarDate(day))}` : ''}`}
            aria-invalid={Boolean(error)} aria-describedby={error ? errorId : undefined}
            className={`${button} date-picker-trigger border border-border bg-transparent`}>
            <IconCalendar aria-hidden="true" size={18} className="shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 text-left">{day ? dateFormat.format(calendarDate(day)) : 'Choose date'}</span>
            <IconChevronDown aria-hidden="true" size={16} className="shrink-0 text-muted-foreground" />
          </PopoverButton>
          <PopoverPanel anchor="bottom start" transition role="dialog" aria-label="Choose date" className="date-picker-popover">
            <ConversationTheme accentColor={palette?.accent} className="date-picker-surface">
              <DateCalendar key={`${day}:${minDay}:${maxDay}`} selected={day} min={minDay} max={maxDay} isAllowed={candidate => isDateQuestionDayAllowed(config, candidate, context)} open={open} disabled={disabled} onSelect={next => { update(next); close(); }} />
            </ConversationTheme>
          </PopoverPanel>
        </>}
      </Popover> : null}
      {mode !== 'date' ? <div className="date-time-selects" role="group" aria-label="Time">
        <IconClock aria-hidden="true" size={18} className="shrink-0 text-muted-foreground" />
        <DateTimeSelect label="Hour" value={hour} disabled={disabled} invalid={Boolean(error)} error={error}
          onChange={next => update(day, next)} options={hours.map(item => ({ value: item, label: item, disabled: !allowedTime(item) }))} />
        <span aria-hidden="true" className="text-muted-foreground">:</span>
        <DateTimeSelect label="Minute" value={minute} disabled={disabled} invalid={Boolean(error)} error={error}
          onChange={next => update(day, hour, next)} options={minutes.map(item => ({ value: item, label: item, disabled: Boolean(hour) && !allowedTime(hour, item) }))} />
        {second !== undefined ? <>
          <span aria-hidden="true" className="text-muted-foreground">:</span>
          <DateTimeSelect label="Second" value={second} disabled={disabled} invalid={Boolean(error)} error={error}
            onChange={next => update(day, hour, minute, next)} options={minutes.map(item => ({ value: item, label: item, disabled: Boolean(hour && minute) && !allowedTime(hour, minute, item) }))} />
        </> : null}
      </div> : null}
    </div>
    <div id={errorId} role="alert" className={error ? 'text-sm text-danger' : 'sr-only'}>{error}</div>
    <button type="button" className={`${button} button-accent inline-flex items-center justify-center px-4 py-3 text-sm`}
      disabled={disabled || !valid} onClick={() => { if (!disabled && valid && value) onSubmit(value); }}>{submitLabel}</button>
  </div>;
}
